import {
  Search,
  Plus,
  Building2,
  ShoppingBag,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { DataTablePagination } from '@/shared/components/data-table'

const PAGE_SIZE = 5

export function VendorTable({
  vendors = [],
  filteredVendors = [],
  search = '',
  onSearchChange,
  isLoading = false,
  onOpenAddVendorModal,
  onCreatePoForVendor,
  onEditVendor,
  onDeleteVendor,
  isAdmin: _isAdmin = false,
}) {
  const [page, setPage] = useState(1)
  const [prevSearch, setPrevSearch] = useState(search)

  if (prevSearch !== search) {
    setPrevSearch(search)
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)

  const paginatedVendors = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE
    return filteredVendors.slice(startIndex, startIndex + PAGE_SIZE)
  }, [filteredVendors, currentPage])
  return (
    <div className="space-y-5">
      {/* Header Banner */}
      <section className="flex flex-col gap-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-xs sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-2xl bg-[#080d25] text-white">
              <Building2 className="size-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#080d25]">
                Vendors
              </h1>
              <p className="text-sm text-slate-500">
                Manage your approved vendors, GST numbers, and contact details.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onOpenAddVendorModal}
          className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#080d25] px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#17203e] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <Plus className="size-4" />
          <span>Add Vendor</span>
        </button>
      </section>

      {/* Table Container */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xs">
        {/* Search & Counter Bar */}
        <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <label className="flex min-w-0 flex-1 items-center gap-3 rounded-full bg-slate-100/80 px-5 py-3 transition focus-within:bg-slate-100 focus-within:ring-2 focus-within:ring-slate-900/10 sm:max-w-xl">
            <Search className="size-4.5 shrink-0 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by company or vendor name..."
              className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
            />
          </label>

          <div className="inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 shadow-xs sm:self-auto">
            <span>
              Showing{' '}
              {filteredVendors.length === 0
                ? 0
                : `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                    currentPage * PAGE_SIZE,
                    filteredVendors.length,
                  )}`}{' '}
              of {vendors.length} vendors
            </span>
          </div>
        </div>

        {/* Content State */}
        {isLoading ? (
          <div className="p-16 text-center text-sm font-medium text-slate-400">
            Loading vendors...
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="p-16 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 mb-3">
              <Building2 className="size-6" />
            </div>
            <p className="font-semibold text-slate-800">
              {search ? 'No matching vendors found' : 'No vendors added yet'}
            </p>
            <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
              {search
                ? 'Try adjusting your search query or clear the filter.'
                : 'Get started by creating your first vendor using the button above.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  {[
                    'Vendor Name',
                    'Phone Number',
                    'Email ID',
                    'Address',
                    'GST Number',
                    'Action',
                  ].map((heading) => (
                    <th
                      key={heading}
                      scope="col"
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {paginatedVendors.map((vendor) => (
                  <tr
                    key={vendor._id}
                    className="transition hover:bg-slate-50/80"
                  >
                    <td className="whitespace-nowrap px-6 py-4.5 text-sm font-semibold text-[#080d25]">
                      {vendor.vendorName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4.5 text-sm text-slate-600">
                      {vendor.phoneNumber}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4.5 text-sm text-slate-600">
                      {vendor.email}
                    </td>
                    <td className="max-w-xs px-6 py-4.5 text-sm text-slate-600 line-clamp-2">
                      {vendor.address}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4.5 text-sm font-mono text-slate-700">
                      {vendor.gstNumber ? (
                        <span className="rounded-md bg-slate-100 px-2 py-1 text-xs">
                          {vendor.gstNumber}
                        </span>
                      ) : (
                        <span className="text-slate-400 font-sans">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4.5">
                      <div className="flex items-center gap-2">
                        {Boolean(onCreatePoForVendor) && (
                          <button
                            type="button"
                            onClick={() => onCreatePoForVendor(vendor)}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-[#080d25] px-3.5 py-2 text-xs font-semibold text-white shadow-xs transition hover:bg-[#17203e]"
                          >
                            <ShoppingBag className="size-3.5" />
                            <span>Create PO</span>
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => onEditVendor(vendor)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                          aria-label={`Edit ${vendor.vendorName}`}
                          title="Edit vendor"
                        >
                          <Pencil className="size-3.5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => onDeleteVendor(vendor)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                          aria-label={`Delete ${vendor.vendorName}`}
                          title="Delete vendor"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && filteredVendors.length > 0 && (
          <DataTablePagination
            page={currentPage}
            pageSize={PAGE_SIZE}
            total={filteredVendors.length}
            onPageChange={setPage}
          />
        )}
      </section>
    </div>
  )
}
